import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ColumnDef } from "@tanstack/react-table";
import { DeleteButton } from "@/components/DeleteButton";
import type { CommentRead } from "@/schemas/comments";
import { NO_DATA } from "@/constants";
import CommentCellWithPopover from "../components/CommentCellWithPopover";
import { formatDateTime } from "@/lib/utils/formatters";

export interface UseCommentsTableColumnOverridesParams {
  draftCommentIds?: Set<string>;
  onRemoveDraft?: (id: string) => void;
  onDraftCommentChange?: (id: string, value: string) => void;
}

export function useCommentsTableColumnOverrides({
  draftCommentIds,
  onRemoveDraft,
  onDraftCommentChange,
}: UseCommentsTableColumnOverridesParams): Record<
  string,
  ColumnDef<CommentRead>
> {
  const { t } = useTranslation();

  return useMemo(() => {
    const hasDrafts = Boolean(draftCommentIds?.size);
    const isDraftRow = (id: string) => draftCommentIds?.has(id) ?? false;

    const overrides: Record<string, ColumnDef<CommentRead>> = {
      created_at: {
        id: "created_at",
        header: t("comments.fields.created_at"),
        cell: ({ row }) => formatDateTime(row.original.created_at),
        enableColumnFilter: false,
        enableSorting: false,
      },
      comment: {
        id: "comment",
        header: t("comments.fields.comment"),
        cell: ({ row }) => {
          const isDraft =
            hasDrafts && onDraftCommentChange && isDraftRow(row.original.id);
          return (
            <CommentCellWithPopover
              key={row.original.id}
              id={row.original.id}
              value={row.original.comment}
              isDraft={isDraft}
              onDraftCommentChange={isDraft ? onDraftCommentChange : undefined}
              placeholder={t("comments.placeholder")}
            />
          );
        },
        enableColumnFilter: false,
        enableSorting: false,
      },
      author_role_name: {
        id: "author_role_name",
        cell: ({ row }) => row.original.author_role_name ?? NO_DATA,
      },
      ...(hasDrafts && draftCommentIds && onRemoveDraft
        ? {
            actions: {
              id: "actions",
              header: t("comments.fields.actions"),
              cell: ({ row }) =>
                isDraftRow(row.original.id) ? (
                  <DeleteButton
                    variant="ghost"
                    size="sm"
                    onClick={() => onRemoveDraft(row.original.id)}
                    label={t("comments.actions.delete")}
                    className="text-gray-500 hover:text-red-600 hover:bg-red-50"
                  />
                ) : null,
              enableColumnFilter: false,
              enableSorting: false,
              size: 80,
              minSize: 80,
            },
          }
        : {}),
    };
    return overrides;
  }, [t, draftCommentIds, onRemoveDraft, onDraftCommentChange]);
}
